import { watch, onMounted, computed } from 'vue';
import useAvailabilityStore from '~/stores/useAvailabilityStore';
import { calculateBopsAvailabilityMap } from '~/modules/availability/utils/calculateBopsAvailabilityMap';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useProductType } from '~/composables/useProductType';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import { useInitUpc, UpcParser as upcParser } from '@nuxt-common/media-card-large/app/composables/useUpc';
import ConsolidatedLatchkeyParser from '~/modules/latchkey/parsers/ConsolidatedLatchkeyParser';
import { toTraitId } from '~/modules/latchkey/utils/utils';
import { getMemberProductMap } from '~/utils/product';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useFulfilmentData } from '~/modules/availability/composables/useFulfilmentData';
import useGetItTodayExperiments from '~/modules/availability/composables/useGetItTodayExperiments';
import { SHIP_IT } from '~/modules/availability/consts/fulfillment';

interface BopsFlags {
  available: boolean;
  bopsAvailability: boolean;
  bossAvailability: boolean;
  storeAvailability: boolean;
  isAnyFulfillmentAvailable?: boolean;
}

interface UpcTarget {
  id: string | number;
  parsedUpcs: ReturnType<typeof upcParser>;
}

export default function useBopsUpcOverride() {
  const availabilityStore = useAvailabilityStore();
  const { productId, productData } = useProduct();
  const { isShopTheSet, isConsolidatedLatchKey, isConsolidatedMaster, isConsolidatedStandard, isConsolidatedPage, isMaster, isUnavailable } =
    useProductType();
  const { isBigTicketItemFlag } = useProductFlags();
  const selectedMemberId = useSelectedMemberId();
  const { killswitches } = useKillswitches();

  const gitExperiments = useGetItTodayExperiments();
  const isChipFulfillmentEnabled = gitExperiments?.isChipFulfillmentEnabled;

  const isFulfillmentDrivenEnabled = computed(() => Boolean(killswitches.isFulfillmentDrivenAvailabilityEnabled && isChipFulfillmentEnabled?.value));

  const fulfilmentData = useFulfilmentData();
  const selectedRadioButton = fulfilmentData?.selectedRadioButton;

  // Executed synchronously in setup() so injectLocal within ConsolidatedLatchkeyParser has active component context
  const baseLatchkeyParsed = isConsolidatedLatchKey && productData ? ConsolidatedLatchkeyParser(productData) : null;

  // Only watch selectedMemberId if the product is Shop The Set; also watch selectedRadioButton if fulfillment-driven availability is enabled
  const watchSources = isShopTheSet
    ? [() => availabilityStore.data, () => selectedMemberId.value, () => (isFulfillmentDrivenEnabled.value ? selectedRadioButton?.value : null)]
    : [() => availabilityStore.data, () => (isFulfillmentDrivenEnabled.value ? selectedRadioButton?.value : null)];

  const getUpdateTargets = (): UpcTarget[] => {
    if (isConsolidatedLatchKey && baseLatchkeyParsed) {
      return baseLatchkeyParsed.latchkeyNames.map((latchkeyName) => ({
        id: toTraitId(productId as string, latchkeyName),
        parsedUpcs: structuredClone(baseLatchkeyParsed.latchkeyUpcMap[latchkeyName]),
      }));
    }

    if (isShopTheSet) {
      // For Shop The Set, the UI isolates interaction to one "selected" member at a time.
      // Rather than eagerly parsing and initializing state for all inactive members,
      // we only parse and override the UPCs for the member currently in focus.
      const currentProductData = productData?.relationships?.memberProductMap?.[selectedMemberId.value];
      return currentProductData ? [{ id: selectedMemberId.value, parsedUpcs: upcParser(currentProductData) }] : [];
    }

    if (isConsolidatedMaster || isConsolidatedStandard || isConsolidatedPage || isMaster) {
      const masterParsedUpcs = upcParser(productData);
      const memberProductMap = getMemberProductMap(productData) || {};

      const memberTargets = Object.entries(memberProductMap).map(([memberId, memberData]) => {
        const hasDirectUpcs = Object.keys(memberData?.relationships?.upcs || {}).length > 0;
        const memberParsedUpcs = hasDirectUpcs
          ? upcParser(memberData as Product)
          : ({
              ...masterParsedUpcs,
              upcs: Object.fromEntries(
                Object.entries(masterParsedUpcs.upcs || {})
                  .filter(([_, upc]) => upc?.relationships?.selectedProduct === memberId)
                  .map(([key, upc]) => [key, structuredClone(upc)]),
              ),
            } as unknown as ReturnType<typeof upcParser>);

        return { id: memberId, parsedUpcs: memberParsedUpcs };
      });

      return isConsolidatedStandard || isConsolidatedMaster
        ? [{ id: productId as string, parsedUpcs: masterParsedUpcs }, ...memberTargets]
        : memberTargets;
    }

    // Update the base product for standard/regular products
    return [{ id: productId as string, parsedUpcs: upcParser(productData) }];
  };

  onMounted(() => {
    if (!killswitches.isBopsUpcAvailabilityUpdateEnabled || isUnavailable || !productData || isBigTicketItemFlag) return;

    watch(
      watchSources,
      () => {
        const newBopsData = availabilityStore.data;
        if (!availabilityStore.isDataLoaded || !newBopsData || Object.keys(newBopsData as object).length === 0) {
          return;
        }

        const targets = getUpdateTargets();

        targets.forEach(({ id, parsedUpcs }) => {
          if (!parsedUpcs || !parsedUpcs.upcs || Object.keys(parsedUpcs.upcs).length === 0) return;

          // 2. Calculate the strict fulfillment map
          const strictAvailabilityMap = calculateBopsAvailabilityMap({
            availabilityData: newBopsData,
            upcData: parsedUpcs,
            selectedRadioButton: isFulfillmentDrivenEnabled.value ? selectedRadioButton?.value || SHIP_IT : undefined,
          });

          // 3. Enrich the parsed data with our strict rules
          Object.entries(strictAvailabilityMap).forEach(([upcKey, bopsFlags]) => {
            const flags = bopsFlags as BopsFlags;
            const upcState = parsedUpcs.upcs[upcKey];

            if (upcState?.availability) {
              // Deep Base Mutation: The BOPS call now fully controls the application state
              upcState.availability.available = flags.available;
              upcState.availability.bopsAvailable = flags.bopsAvailability;
              upcState.availability.bossAvailable = flags.bossAvailability;
              upcState.availability.storeAvailability = flags.storeAvailability;
              upcState.availability.isAnyFulfillmentAvailable = flags.isAnyFulfillmentAvailable;
            }
          });

          // 4. Natively re-initialize MCL with the enriched data!
          useInitUpc({
            id,
            data: {
              ...parsedUpcs,
              isBopsUpcAvailabilityUpdateEnabled: killswitches.isBopsUpcAvailabilityUpdateEnabled,
            },
          });
        });
      },
      { deep: true, immediate: true },
    );
  });
}
